import React, { useRef, useEffect, useState } from "react";
import { useLanguage } from "../../../context/LanguageContext";
import "./ShopFilterPanel.css";

const ShopFilterPanel = ({
    show,
    onClose,
    categories,
    selectedCategory,
    onCategoryChange,
    selectedColor,
    onColorChange,
    priceRange,
    onPriceChange
}) => {
    const { language } = useLanguage();
    const panelRef = useRef();
    const [visible, setVisible] = useState(show);
    const [animateOut, setAnimateOut] = useState(false);

    // Translations for filter panel
    const translations = {
        en: {
            filterSort: "Filter & Sort",
            category: "Category",
            all: "All",
            color: "Color",
            black: "Black",
            white: "White",
            red: "Red",
            blue: "Blue",
            green: "Green",
            maxPrice: "Max Price",
            apply: "APPLY"
        },
        ar: {
            filterSort: "فلتر وترتيب",
            category: "الفئة",
            all: "الكل",
            color: "اللون",
            black: "أسود",
            white: "أبيض",
            red: "أحمر",
            blue: "أزرق",
            green: "أخضر",
            maxPrice: "أقصى سعر",
            apply: "تطبيق"
        }
    };

    const t = translations[language] || translations.en;

    // Helper function to get localized category name
    const getLocalizedCategoryName = (category) => {
        if (category.multilingual && category.multilingual[language] && category.multilingual[language].name) {
            return category.multilingual[language].name;
        }
        // Fallback to default name or English name
        return category.name || (category.multilingual?.en?.name) || '';
    };

    // Helper function to get the original English category name for filtering
    const getOriginalCategoryName = (category) => {
        // Return the original name field (which is English) or the English multilingual name
        return category.originalName || category.multilingual?.en?.name || category.name;
    };

    // Show panel when `show` becomes true
    useEffect(() => {
        if (show) setVisible(true);
    }, [show]);

    // Close on outside click
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (panelRef.current && !panelRef.current.contains(e.target)) {
                handleClose();
            }
        };

        if (visible) {
            document.addEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [visible]);

    // Animate close
    const handleClose = () => {
        setAnimateOut(true);
        setTimeout(() => {
            setAnimateOut(false);
            setVisible(false);
            onClose();
        }, 300); // match the animation duration
    };

    if (!visible) return null;

    return (
        <div className="shop-filter-overlay">
            <div
                className={`shop-filter-panel ${animateOut ? "slide-out" : "slide-in"}`}
                ref={panelRef}
            >
                <div className="shop-filter-header">
                    <span>{t.filterSort}</span>
                    <button onClick={handleClose} className="closeS-btn">&times;</button>
                </div>

                <div className="filter-group">
                    <label>{t.category}</label>
                    <select value={selectedCategory} onChange={(e) => onCategoryChange(e.target.value)}>
                        <option value="">{t.all}</option>
                        {(categories || []).map((cat) => (
                            <option key={cat.id} value={getOriginalCategoryName(cat)}>
                                {getLocalizedCategoryName(cat)}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="filter-group">
                    <label>{t.color}</label>
                    <select value={selectedColor} onChange={(e) => onColorChange(e.target.value)}>
                        <option value="">{t.all}</option>
                        <option value="black">{t.black}</option>
                        <option value="white">{t.white}</option>
                        <option value="red">{t.red}</option>
                        <option value="blue">{t.blue}</option>
                        <option value="green">{t.green}</option>
                    </select>
                </div>

                <div className="filter-group">
                    <label>{t.maxPrice}: {priceRange} AED</label>
                    <input type="range" min="1" max="2000" value={priceRange} onChange={onPriceChange} />
                </div>

                <div className="apply-button-wrapper">
                    <button onClick={handleClose} className="apply-button">{t.apply}</button>
                </div>
            </div>
        </div>
    );
};

export default ShopFilterPanel;